export function formatSectionHeader(typeLabel) {
  const label = typeLabel.toUpperCase();
  return `${label}\n${'='.repeat(label.length)}\n`;
}

export function formatCopyPayload(typeLabel, allAnswers, rtl = false) {
  let payload = formatSectionHeader(typeLabel);
  
  allAnswers.forEach(({ sectionName, answers }) => {
    if (sectionName) {
      payload += `${sectionName}\n`;
    }
    payload += answers.map(a => ` • ${a}`).join('\n') + '\n';
  });
  
  payload = payload.trim();
  return rtl ? '\u200F' + payload : payload;
}

export class DomContentExtractor {
  constructor(parentCard) {
    this.parentCard = parentCard;
  }

  getSectionName() {
    const title = this.parentCard.querySelector('.v-card__title');
    if (!title) return 'Unknown Section';
    const text = title.textContent || title.innerText;
    return text.trim();
    }

  _extractText(selector) {
    return Array.from(this.parentCard.querySelectorAll(selector))
      .map(el => (el.textContent || '').trim())
      .filter(Boolean);
  }

  extractFromSubtitles() {
    return this._extractText('.v-card__subtitle');
  }

  extractFromListItemContents() {
    return this._extractText('.v-list-item__content');
  }

  extractFromListItemTitlesAndSubtitles() {
    const titles = this.parentCard.querySelectorAll('.v-list-item__title');
    const subtitles = this.parentCard.querySelectorAll('.v-list-item__subtitle');
    
    return Array.from(
      { length: Math.max(titles.length, subtitles.length) },
      (_, i) => {
        const t = titles[i]?.innerText.trim() || '';
        const s = subtitles[i]?.innerText.trim() || '';
        return t && s ? `${t}: ${s}` : t || s;
      }
    ).filter(Boolean);
  }

  extractFromCardText() {
    const cardText = this.parentCard.querySelector('.v-card__text');
    if (!cardText) return [];
    
    const clone = cardText.cloneNode(true);
    clone.querySelectorAll('button, .v-btn, .v-divider').forEach(el => el.remove());
    
    return clone.innerText
      .trim()
      .split('\n')
      .map(l => l.trim())
      .filter(Boolean);
  }

  extractAnswers() {
    const strategies = [
      this.extractFromSubtitles,
      this.extractFromListItemContents,
      this.extractFromListItemTitlesAndSubtitles,
      this.extractFromCardText
    ];
    
    for (const strategy of strategies) {
      try {
        const answers = strategy.call(this);
        if (answers.length) {
          return this.cleanAnswers(answers);
        }
      } catch (error) {
        console.warn('Strategy failed, trying next:', error);
        continue;
      }
    }
    return [];
  }

  cleanAnswers(answers) {
    return [
      ...new Set(
        answers.flatMap(a =>
          a.split(/\n/).flatMap(p => p.split(/ {2,}/))
            .map(p => p.trim())
            .filter(Boolean)
        )
      )
    ];
  }
}

export class SummaryPayloadBuilder {
  constructor(vueComponent = {}, rtl = false) {
    this.vueComponent = vueComponent;
    this.collections = vueComponent.collections || {};
    this.$refs = vueComponent.$refs || {};
    this.rtl = rtl;
    this.$t = typeof vueComponent.$t === 'function'
      ? vueComponent.$t.bind(vueComponent)
      : k => k;
  }

  safeT(key) {
    try {
      return this.$t(key);
    } catch {
      return key;
    }
  }

  getListComponent(type, index) {
    const ref = this.$refs[`${type}List${index}`];
    return ref ? (Array.isArray(ref) ? ref[0] : ref) : null;
  }

  getParentCard(listComponent) {
    return listComponent?.$el?.closest('.v-card') || null;
  }

  extractPanelContent(type, panel, index) {
    const parentCard = this.getParentCard(this.getListComponent(type, index));
    if (!parentCard) return null;
    
    const extractor = new DomContentExtractor(parentCard);
    const answers = extractor.extractAnswers();
    
    return answers.length
      ? { sectionName: extractor.getSectionName(), answers }
      : null;
  }

  buildPayloadForType(type) {
    const typeLabel = this.safeT(`app.summary.${type}.title`);
    const allAnswers = (this.collections[type] || [])
      .map((panel, i) => this.extractPanelContent(type, panel, i))
      .filter(Boolean);
    
    return formatCopyPayload(typeLabel, allAnswers, this.rtl);
  }

  buildAllPayloads() {
    return Object.fromEntries(
      Object.keys(this.collections).map(type => [
        type,
        this.buildPayloadForType(type).trim()
      ])
    );
  }

  static getPreferredOrder() {
    return [
      'patient_demographics',
      'patient',
      'observations',
      'assessment'
    ];
  }

  buildCombinedPayload(allPayloads = {}) {
    if (!allPayloads || typeof allPayloads !== 'object') {
      return '';
    }
    const order = SummaryPayloadBuilder.getPreferredOrder();
    let payload = '';
    
    const orderedTypes = [
      ...order,
      ...Object.keys(allPayloads).filter(t => !order.includes(t) && t !== 'unknown')
    ];
    
    orderedTypes.forEach(t => {
      if (allPayloads[t]) {
        payload += allPayloads[t] + '\n\n';
      }
    });
    
    return payload.trim();
  }
}